<template>
    <base-view :base-store="applicationStore" :title="$t('appMgmt.title')">
        <template #content>
            <crud-table
                class="ma-6"
                :after-create="[applicationStore.reload]"
                :base-store="applicationStore"
                :default-item="defaultItem"
                :table-headers="companyTableHeaders"
                :edit-title="$t('appMgmt.editTitle')"
                :create-btn-title="$t('appMgmt.createBtnTitle')"
                :create-title="$t('appMgmt.createTitle')"
                :delete-confirm-text="$t('remove')"
                :delete-abort-text="$t('cancel')"
                :delete-title="$t('appMgmt.deleteTitle')"
                suppress-delete-cancel-message
                suppress-delete-confirm-message
                suppress-edit-cancel-message
                suppress-edit-confirm-message
            >
                <template #deleteForm>
                    <p>{{ $t('appMgmt.deleteText') }}</p>
                </template>
                <template #editForm="{focusItem, outprops, onSave, onCancel}">

                    <v-row v-if="focusItem.guid">
                        <text-field :model-value="focusItem.guid"
                                    prepend-icon="mdi-identifier"
                                    :label="$t('appMgmt.table.guid')"
                                    readonly />
                        <text-field :model-value="focusItem.api_key"
                                    prepend-icon="mdi-key"
                                    :label="$t('appMgmt.table.api_key')"
                                    readonly />
                    </v-row>
                    <v-row>
                        <text-field v-model="focusItem.name"
                                    prepend-icon="mdi-application"
                                    :hint="$t('appMgmt.name.hint')"
                                    :label="$t('appMgmt.name.lbl')"/>
                        <text-field v-model="focusItem.resource_group"
                                    prepend-icon="mdi-semantic-web"
                                    :hint="$t('appMgmt.resource_group.hint')"
                                    :label="$t('appMgmt.resource_group.lbl')"/>
                        <text-field v-model="focusItem.auth_type"
                                    type="select"
                                    item-title="name"
                                    item-value="value"
                                    prepend-icon="mdi-two-factor-authentication"
                                    :select-items="[{'name':'Token', 'value': 'token'}]"
                                    :hint="$t('appMgmt.auth_type.hint')"
                                    :label="$t('appMgmt.auth_type.lbl')"/>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-select
                                v-model="focusItem.services"
                                :items="serviceOptions"
                                item-title="title"
                                item-value="value"
                                :label="$t('appMgmt.services.lbl')"
                                :hint="$t('appMgmt.services.hint')"
                                prepend-icon="mdi-puzzle"
                                multiple
                                chips
                                closable-chips
                                persistent-hint
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-select
                                v-model="focusItem.permissions"
                                :items="permissionOptions"
                                item-title="title"
                                item-value="value"
                                :label="$t('appMgmt.permissions.lbl')"
                                :hint="$t('appMgmt.permissions.hint')"
                                prepend-icon="mdi-shield-key"
                                multiple
                                chips
                                closable-chips
                                persistent-hint
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-text-field
                                type="date"
                                :model-value="focusItem.valid_from instanceof Date ? focusItem.valid_from.toISOString().slice(0, 10) : focusItem.valid_from"
                                @update:model-value="v => focusItem.valid_from = v ? new Date(v + 'T00:00:00') : undefined"
                                :hint="$t('appMgmt.valid_from.hint')"
                                :label="$t('appMgmt.valid_from.lbl')"
                                persistent-hint
                            />
                        </v-col>
                        <v-col>
                            <v-text-field
                                type="date"
                                :model-value="focusItem.valid_until instanceof Date ? focusItem.valid_until.toISOString().slice(0, 10) : focusItem.valid_until"
                                @update:model-value="v => focusItem.valid_until = v ? new Date(v + 'T00:00:00') : undefined"
                                :hint="$t('appMgmt.valid_until.hint')"
                                :label="$t('appMgmt.valid_until.lbl')"
                                persistent-hint
                            />
                        </v-col>
                    </v-row>
                    <action-btn :confirm-text="$t('save')"
                                :cancel-text="$t('cancel')"
                                @save="onSave"
                                @cancel="onCancel"/>
                </template>
            </crud-table>
        </template>
    </base-view>
</template>

<script lang="ts">
import {computed, defineComponent} from "vue"
import {useApplicationStore} from "@/stores/ApplicationStore/ApplicationStore";
import CrudTable from "@/components/generics/CrudTable/CrudTable.vue";
import type {IApplication} from "@/stores/ApplicationStore/interfaces/IApplication";
import {useDates} from "@/composables/useDates";
import BaseView from "@/views/BaseView.vue";
import ActionBtn from "@/components/generics/ActionBtn/ActionBtn.vue";
import TextField from "@/components/generics/TextField/TextField.vue";
import {useI18n} from "vue-i18n";
import {ADMIN_ROLE, USER_ROLE, FRONTEND_ROLE, APP_ADMIN_ROLE} from "@/composables/useAppConst";


export default defineComponent({
    components: {
        TextField,
        ActionBtn,
        BaseView,
        CrudTable,
    },
    setup() {
        const applicationStore = useApplicationStore()
        const today = new Date();
        const future = new Date();
        future.setDate(today.getDate() - 180);

        const {t} = useI18n();
        const {getDate} = useDates();
        const defaultItem: Omit<IApplication, 'id' | 'guid' | 'api_key'> = {
            name: "",
            valid_from: today,
            valid_until: future,
            auth_type: "token",
            resource_group: "",
            services: ["agent", "workflow", "guardrails", "knowledge_base", "developer_api"],
            permissions: [USER_ROLE, FRONTEND_ROLE],
        }

        const serviceOptions = computed(() => [
            {title: t('appMgmt.services.agent'), value: 'agent'},
            {title: t('appMgmt.services.workflow'), value: 'workflow'},
            {title: t('appMgmt.services.workflow_assistant'), value: 'workflow_assistant'},
            {title: t('appMgmt.services.guardrails'), value: 'guardrails'},
            {title: t('appMgmt.services.knowledge_base'), value: 'knowledge_base'},
            {title: t('appMgmt.services.developer_api'), value: 'developer_api'},
            {title: t('appMgmt.services.app_chat'), value: 'app_chat'},
            {title: t('appMgmt.services.agentic_workflows'), value: 'agentic_workflows'},
        ])

        const permissionOptions = computed(() => [
            {title: t('appMgmt.permissions.admin'), value: ADMIN_ROLE},
            {title: t('appMgmt.permissions.application'), value: USER_ROLE},
            {title: t('appMgmt.permissions.frontend'), value: FRONTEND_ROLE},
            {title: t('appMgmt.permissions.app_admin'), value: APP_ADMIN_ROLE},
        ])

        const companyTableHeaders = computed(() => [
            {title: t('appMgmt.table.name'), align: 'start', sortable: true, key: 'name'},
            {title: t('appMgmt.table.guid'), align: 'start', sortable: true, key: 'guid'},
            {title: t('appMgmt.table.api_key'), align: 'start', sortable: false, key: 'api_key'},
            {title: t('appMgmt.table.resource_group'), align: 'start', sortable: true, key: 'resource_group'},
            {title: t('appMgmt.table.actions'), align: 'end', sortable: false, key: 'Actions'},
        ])

        return {
            // composable
            applicationStore,
            getDate,
            // const
            defaultItem,
            serviceOptions,
            permissionOptions,
            // computed
            companyTableHeaders
        }
    },
})
</script>
